import { parseWorldSessionSelectionReceipt } from "@cleo/shared/sessionSelection";
import { readRoomUniverseSelection } from "./roomUniverseStore";
import { postLocalJson } from "./localJsonTransport";
import { useNeucleosWorldStore } from "./worldStore";

export interface BoardContextBirthCoordinate {
  readonly worldKey: string;
  readonly roomId: string;
  readonly sessionId: string;
  readonly agentEnvironmentId: string;
  readonly agentThreadId: string;
}

export type BoardContextBirthOutcome =
  | { readonly ok: true; readonly memberIds: ReadonlyArray<string>; readonly changed: boolean }
  | { readonly ok: false; readonly reason: string };

export interface WorldSourceRoot {
  readonly group: "root-codebase" | "world-files";
  readonly memberId: string;
  readonly label: string;
  readonly path: string;
}

/** THE SESSION LAW, step 2: the World's listed codebase and file folder. The
 * host answers each root's physical folder reference; an absent or unreadable
 * root is simply not listed. */
export async function readWorldSourceRoots(
  worldKey: string,
  fetchImpl: typeof fetch,
): Promise<ReadonlyArray<WorldSourceRoot>> {
  try {
    const response = await postLocalJson(
      "/api/neucleos/world-source-roots",
      { worldKey },
      fetchImpl,
    );
    const body = await jsonRecord(response);
    const roots = body?.["roots"];
    if (!response.ok || body?.["ok"] !== true || !Array.isArray(roots)) return [];
    return roots.flatMap((root) => {
      if (typeof root !== "object" || root === null) return [];
      const record = root as Record<string, unknown>;
      return (record["group"] === "root-codebase" || record["group"] === "world-files") &&
        typeof record["memberId"] === "string" &&
        typeof record["label"] === "string" &&
        typeof record["path"] === "string"
        ? [
            {
              group: record["group"],
              memberId: record["memberId"],
              label: record["label"],
              path: record["path"],
            },
          ]
        : [];
    });
  } catch {
    return [];
  }
}

/** A root enters the boundary as a searchable folder reference, never as injected content. */
export const rootFolderMoves = (roots: ReadonlyArray<WorldSourceRoot>) =>
  roots.map((root) => ({
    memberId: root.memberId,
    kind: "folder" as const,
    direction: "in" as const,
    delivery: "reference" as const,
  }));

/** A newborn session: born, no gesture yet, nothing selected. Only such a
 * session receives the listed roots; any later cut is the person's choice. */
export function isNewbornSelection(selection: {
  readonly selectionCut: number;
  readonly memberIds: ReadonlyArray<string>;
  readonly expressions?: ReadonlyArray<unknown> | undefined;
  readonly birth?: { readonly status: string; readonly cut: number } | undefined;
}): boolean {
  return (
    selection.birth?.status === "ready" &&
    selection.selectionCut === selection.birth.cut &&
    selection.memberIds.length === 0 &&
    (selection.expressions?.length ?? 0) === 0
  );
}

async function jsonRecord(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const value: unknown = await response.json();
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Initialize only listed World folders. Repair legacy pending births through
 * the same door without admitting their obsolete implicit content policy. */
async function initializeStoredBoardContextBirth(
  input: BoardContextBirthCoordinate & {
    readonly boardKey?: string | null;
    readonly fetchImpl?: typeof fetch;
  },
): Promise<BoardContextBirthOutcome> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const authority = useNeucleosWorldStore.getState();
  const isCurrent = () => {
    if (input.fetchImpl !== undefined) return true;
    const now = useNeucleosWorldStore.getState();
    const grant = now.worlds.find((world) => world.worldKey === input.worldKey);
    return (
      now.identity === authority.identity &&
      now.signedIn === authority.signedIn &&
      now.grantRevision === authority.grantRevision &&
      now.activeWorldKey === authority.activeWorldKey &&
      (!now.loaded ||
        (now.signedIn && grant?.mcpTokenPresent === true && grant.expiresAt > Date.now()))
    );
  };
  const changedAuthority = {
    ok: false as const,
    reason: "The active World or its access changed.",
  };
  const coordinate: BoardContextBirthCoordinate = {
    worldKey: input.worldKey,
    roomId: input.roomId,
    sessionId: input.sessionId,
    agentEnvironmentId: input.agentEnvironmentId,
    agentThreadId: input.agentThreadId,
  };
  const readSelection = async (afterSelectionCut?: number) => {
    if (!isCurrent()) return changedAuthority;
    if (input.fetchImpl === undefined)
      return readRoomUniverseSelection(coordinate, {
        ...(afterSelectionCut === undefined ? {} : { force: true, afterSelectionCut }),
        authorizationRevision: authority.grantRevision,
        isCurrent,
      });
    const response = await postLocalJson("/api/neucleos/context-selection", coordinate, fetchImpl);
    const body = await jsonRecord(response);
    const value = body?.["selection"] as Record<string, unknown> | undefined;
    const parsed =
      value && Array.isArray(value["memberIds"])
        ? parseWorldSessionSelectionReceipt(
            {
              ...value,
              count: value["memberIds"].length,
              ...(Array.isArray(value["expressions"])
                ? { expressionCount: value["expressions"].length }
                : {}),
            },
            coordinate,
          )
        : null;
    if (!response.ok || body?.["ok"] !== true || parsed?.kind !== "inline")
      return {
        ok: false as const,
        reason:
          typeof body?.["reason"] === "string"
            ? body["reason"]
            : "The session's stored selection could not be read.",
      };
    return { ok: true as const, selection: parsed.selection };
  };
  try {
    const initial = await readSelection();
    if (!initial.ok) return initial;
    if (!isCurrent()) return changedAuthority;
    const selection = initial.selection;
    const birth = selection.birth;
    if (birth?.status !== "pending") {
      // THE SESSION LAW, step 2: a session starts with zero, then the World's
      // listed codebase and file folder enter its boundary as folders. Once,
      // at birth, never again: any later cut is the person's own choice.
      if (!isNewbornSelection(selection)) {
        return { ok: true, memberIds: selection.memberIds, changed: false };
      }
      const roots = await readWorldSourceRoots(input.worldKey, fetchImpl);
      if (!isCurrent()) return changedAuthority;
      if (roots.length === 0) return { ok: true, memberIds: selection.memberIds, changed: false };
      const admitResponse = await postLocalJson(
        "/api/neucleos/context-selection/move-batch",
        {
          ...coordinate,
          moves: rootFolderMoves(roots),
          expectedSelectionCut: selection.selectionCut,
          expectedSelectionDigest: selection.selectionDigest,
        },
        fetchImpl,
      );
      const admitBody = await jsonRecord(admitResponse);
      if (!admitResponse.ok || admitBody?.["ok"] !== true) {
        // Another client of the same person may have admitted the roots first;
        // the stored selection, not this attempt, is the truth.
        const again = await readSelection(selection.selectionCut);
        if (!isCurrent()) return changedAuthority;
        return again.ok
          ? { ok: true, memberIds: again.selection.memberIds, changed: false }
          : again;
      }
      const admitted = await readSelection(selection.selectionCut);
      if (!admitted.ok) return admitted;
      if (!isCurrent()) return changedAuthority;
      return { ok: true, memberIds: admitted.selection.memberIds, changed: true };
    }
    // Legacy pending births predate the session law. Preserve their cut and
    // acknowledge them once with only the allowed World folders; never enumerate
    // or admit a room's content catalog while repairing the old birth.
    const roots = await readWorldSourceRoots(input.worldKey, fetchImpl);
    if (!isCurrent()) return changedAuthority;
    const moveResponse = await postLocalJson(
      "/api/neucleos/context-selection/move-batch",
      {
        ...coordinate,
        moves: rootFolderMoves(roots),
        replace: true,
        birthInitializationCut: birth.cut,
        expectedSelectionCut: selection.selectionCut,
        expectedSelectionDigest: selection.selectionDigest,
      },
      fetchImpl,
    );
    const moveBody = await jsonRecord(moveResponse);
    if (!moveResponse.ok || moveBody?.["ok"] !== true)
      return {
        ok: false,
        reason:
          typeof moveBody?.["reason"] === "string"
            ? moveBody["reason"]
            : "The World did not confirm the board's initial Context.",
      };
    const readback = await readSelection(selection.selectionCut);
    if (!readback.ok) return readback;
    if (!isCurrent()) return changedAuthority;
    if (readback.selection.birth?.status !== "ready" || readback.selection.birth.cut !== birth.cut)
      return { ok: false, reason: "The session's initial selection is still pending." };
    return { ok: true, memberIds: readback.selection.memberIds, changed: true };
  } catch {
    return { ok: false, reason: "The local Context door was unreachable." };
  }
}

const pendingInitializations = new Map<string, Promise<BoardContextBirthOutcome>>();

/** Launchers and an adopted session can meet while the same birth is settling. */
export function initializeBoardContextBirth(
  input: BoardContextBirthCoordinate & {
    readonly boardKey?: string | null;
    readonly fetchImpl?: typeof fetch;
  },
): Promise<BoardContextBirthOutcome> {
  if (input.fetchImpl !== undefined) return initializeStoredBoardContextBirth(input);
  const authority = useNeucleosWorldStore.getState();
  const key = [
    input.worldKey,
    input.roomId,
    input.sessionId,
    input.agentEnvironmentId,
    input.agentThreadId,
    authority.identity,
    authority.signedIn,
    authority.grantRevision,
    authority.activeWorldKey,
  ].join("\u0000");
  const pending = pendingInitializations.get(key);
  if (pending) return pending;
  const work = initializeStoredBoardContextBirth(input).finally(() => {
    if (pendingInitializations.get(key) === work) pendingInitializations.delete(key);
  });
  pendingInitializations.set(key, work);
  return work;
}
